/**
 * FiasFilePickerDialog — pick an existing PDF from the user's Vault
 * Documents and hand it back as a `File` blob for the document-sign
 * upload pipeline. Wraps the shared `VaultFilePickerDialog` with a
 * PDF mime-filter; previously a dual-tab "My Data" + "Vault" picker,
 * collapsed to single-source after the user_files system was retired.
 */

import { useCallback, useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import { VaultFilePickerDialog, type VaultPickerResult } from '@/components/VaultFilePickerDialog';

const DOCUMENT_SIGN_SOURCE_MIME_TYPE = 'application/pdf';

function ensurePdfExtension(name: string): string {
  return /\.pdf$/i.test(name) ? name : `${name}.pdf`;
}

interface FiasFilePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFilePicked: (file: File) => void;
}

export function FiasFilePickerDialog({
  open,
  onOpenChange,
  onFilePicked,
}: FiasFilePickerDialogProps) {
  const [downloading, setDownloading] = useState(false);

  const handlePicked = useCallback(
    async (picked: VaultPickerResult) => {
      setDownloading(true);
      try {
        const response = await fetch(picked.downloadUrl);
        if (!response.ok) {
          throw new Error('The file could not be downloaded.');
        }
        const blob = await response.blob();
        const file = new File([blob], ensurePdfExtension(picked.fileName), {
          type: DOCUMENT_SIGN_SOURCE_MIME_TYPE,
        });
        onFilePicked(file);
      } catch (err) {
        toast({
          title: 'Failed to attach file',
          description: err instanceof Error ? err.message : 'An error occurred',
          variant: 'destructive',
        });
      } finally {
        setDownloading(false);
      }
    },
    [onFilePicked],
  );

  return (
    <VaultFilePickerDialog
      open={open}
      onOpenChange={(next) => {
        if (downloading) return;
        onOpenChange(next);
      }}
      title="Add a PDF from Fias"
      description="Choose an existing PDF from your storage to attach to this envelope."
      mimeFilter={(doc) => doc.mimeType === 'application/pdf'}
      uploadAccept="application/pdf"
      confirmLabel="Attach"
      onFileSelected={handlePicked}
    />
  );
}
